import { Controller } from '@hotwired/stimulus'
import { api } from '../../api'
import { SubmissionFormType } from '../../types/common'
import FormChangeController from './form-change'
import PartialFormChangesController from './partial-form-changes'
import { StreamEvent, StreamEventDetail } from './stream-manager'
import SubmissionController, { SetStatusEvent } from './submission'

export type Action = 'edit' | 'new' | 'show' | 'update'

interface HandleDeleteEvent extends Event {
  detail: {
    submissionId: string
  }
}
export default class SubmissionContainerController extends Controller<HTMLElement> {
  declare actionValue: Action
  declare containerSessionIdValue: string
  declare idValue: string
  declare identityIdValue: string
  declare readonly footerActionsTarget: HTMLElement
  declare readonly formChangeOutlet: FormChangeController
  declare readonly hasFooterActionsTarget: boolean
  declare readonly hasFormChangeOutlet: boolean
  declare readonly hasSubmissionOutlet: boolean
  declare readonly hasSubmitButtonTarget: boolean
  declare readonly statusTarget: HTMLInputElement
  declare readonly submissionOutlets: SubmissionController[]
  declare readonly submitButtonTarget: HTMLButtonElement

  static outlets = ['form-change', 'submission']

  static targets = ['footerActions', 'status', 'submitButton']

  static values = {
    action: String,
    containerSessionId: String,
    id: String,
    identityId: String,
  }

  #submitting = false

  // Getters

  get formType(): SubmissionFormType {
    return this.hasSubmissionOutlet ? this.submissionOutlets.at(0)!.formTypeValue : 'NewBusinessSubmission'
  }

  get lockedSubmissions() {
    return this.hasSubmissionOutlet ? this.submissionOutlets.filter(submission => submission.isLocked) : []
  }

  get isEditing() {
    return this.actionValue === 'edit'
  }

  // Lifecycle methods

  connect() {
    this.dispatch('opened')
    //FIXME: submission-record might be more appropriate, but some rewiring is needed in the BE
    document.addEventListener('stream-manager:render-record', this.#handleSubmissionRecordStream)
    // Each panel answers for itself through `partial-form-changes`, and the
    // event bubbles up to here.
    this.element.addEventListener('partial-form-changes:changed', this.#syncFooter)
  }

  disconnect() {
    document.removeEventListener('stream-manager:render-record', this.#handleSubmissionRecordStream)
    this.element.removeEventListener('partial-form-changes:changed', this.#syncFooter)
  }

  // Outlet and target callbacks

  submissionOutletConnected() {
    this.#syncFooter()
  }

  submissionOutletDisconnected() {
    this.#syncFooter()
  }

  formChangeOutletConnected(formChange: FormChangeController) {
    formChange.element.addEventListener('form-change:changed', this.#syncFooter)
    this.#syncFooter()
  }

  formChangeOutletDisconnected(formChange: FormChangeController) {
    formChange.element.removeEventListener('form-change:changed', this.#syncFooter)
  }

  // A streamed-in footer arrives in whatever state the server rendered it, so
  // it is brought up to date the moment it connects.
  footerActionsTargetConnected() {
    this.#syncFooter()
  }

  // Public methods

  // The multi-submission drawer names its container session, which lets the
  // server clean up everything the drawer held in one request.
  async cleanup() {
    if (this.containerSessionIdValue) return this.#endContainerSession()

    await this.#endReviewSession()
  }

  handleDelete({ detail: { submissionId } }: HandleDeleteEvent) {
    const submission = this.submissionOutlets.find(submission => submission.idValue === submissionId)
    if (!submission) return

    // The Submission and its associated Lock have been deleted by this point.
    // Let's clear the `lockId` value to prevent the cleanup phase from
    // attempting to unlock the Submission as the drawer closes.
    submission.lockIdValue = ''
  }

  // Leaving the page. Downloading an attachment can fire beforeunload without
  // leaving, so skip that, as locking.js does.
  leave() {
    if (document.activeElement?.closest('[data-attachment]')) return

    this.cleanup()
  }

  setStatus(event: SetStatusEvent) {
    if (this.hasSubmissionOutlet) {
      for (const submission of this.submissionOutlets) {
        submission.setStatus(event)
      }
    }
  }

  submitEnded() {
    this.#submitting = false
  }

  submitStarted() {
    this.#submitting = true
  }

  // Private methods

  // The footer stays on screen for anything the advisor would lose by closing
  // the drawer -- a card open for editing, or a panel holding a staged
  // attachment or a typed message. Only an open card can be submitted, though,
  // so staged panel content alone leaves Submit there but disabled.
  //
  // `form-change` already tracks uploads across every attachment manager in the
  // drawer, so that is read rather than worked out again here.
  #syncFooter = () => {
    const open = this.lockedSubmissions.length > 0
    const staged = Boolean(this.element.querySelector(PartialFormChangesController.MODIFIED_SELECTOR))
    const busy = this.hasFormChangeOutlet && this.formChangeOutlet.disabledValue

    if (this.hasFooterActionsTarget) this.footerActionsTarget.hidden = !open && !staged
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = !open || busy
  }

  #endContainerSession() {
    // Submit deletes the locks itself; deleting them mid-save would break it.
    if (this.#submitting) return

    api.containerSessions
      .destroy({ id: this.containerSessionIdValue, submissionContainerId: this.idValue })
      .catch(() => {})
  }

  async #endReviewSession() {
    const results = await Promise.allSettled(this.lockedSubmissions.map(submission => submission.unlock()))

    if (results.some(result => result.status === 'rejected')) {
      console.error('Could not unlock all Submissions', { results })
    }
  }

  #handleSubmissionRecordStream = (event: StreamEvent) => {
    const { streamElement } = event.detail
    switch (streamElement.action) {
      case 'replace':
      case 'prepend':
        this.#submissionRecordAction(event.detail)
        break
      default:
      // No default by design
    }
  }

  #submissionRecordAction({ originalRender, streamElement, fragment, upstreamEvent }: StreamEventDetail) {
    this.#trackChanges(fragment)
    originalRender(streamElement)

    upstreamEvent.preventDefault()
  }

  #trackChanges(fragment: HTMLElement) {
    const changedElements = Array.from(fragment.querySelectorAll('.broadcasted-field'))

    changedElements.forEach(element => {
      const record = element.closest('.new-business-record')
      if (record) record.classList.add('animate-record')
    })
  }
}
